import React from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { SubscriptionUpdatePaymentModal } from '@fff-web/fff-payments';
import { Constants, HCaptchaContainer } from '@fff-web/fff-payments';
import { identify, trackEvent } from '@fff-web/fff-analytics';
import Layout from '../../components/Layout';
import AllErrorMessage from '../../components/AllErrorMessage';
import useShowErrorOrRedirectThankYouPage from '../../hooks/useShowPayErrorOrThankYouPage';
import { useUpgradeConfirmationState } from './useUpgradeConfirmationState';
import {
  PRO_RATED_UPGRADE_MESSAGES,
  trackSubscriptionUpgrade,
} from './ProRatedUpgradeUtils';
import { ProRatedUpgrade } from './ProRatedUpgrade';
import { executeCaptcha } from '../../utils/functions/executeCaptcha';
import { useTrackProRatedUpgrade } from './ProRatedUpgradeUtils';
import {
  redirectToBaseSite,
  redirectToLogin,
} from '../../utils/redirectMethods';

function ProRatedUpgradeContainer() {
  const {
    loading,
    setLoading,
    captchaRef,
    prices,
    userInfo,
    history,
    couponCode,
  } = useUpgradeConfirmationState();

  const {
    showRetry,
    showPayment,
    errorPaymentMessage,
    changeSubscription,
    onClose,
    isLoading,
  } = useShowErrorOrRedirectThankYouPage(
    history,
    PRO_RATED_UPGRADE_MESSAGES.thanksPage,
    Constants.SEASONAL_PLAN_CODE,
    [{ type: 'coupon_code', code: couponCode }]
  );

  useTrackProRatedUpgrade();

  const onSubmitSubscription = async () => {
    try {
      const captchaToken = await executeCaptcha(captchaRef);
      setLoading(true);

      await changeSubscription(Constants.ANNUAL_PLAN_CODE, captchaToken);

      identify({
        userId: userInfo.user.userId.toString(),
        traits: {
          planCode: userInfo.user.planCode,
          memberStatus: userInfo.user.subscriptionState.toLowerCase(),
          role: userInfo.user.subscriptionState.toLowerCase(),
        },
      });

      trackSubscriptionUpgrade({
        ctaName: PRO_RATED_UPGRADE_MESSAGES.ctaName,
        trackName: PRO_RATED_UPGRADE_MESSAGES.trackName,
        newPlan: Constants.ANNUAL_PLAN_CODE,
        previousPlan: userInfo.user.subscriptionType,
      });
    } catch (error) {
      datadogLogs.logger.error(
        PRO_RATED_UPGRADE_MESSAGES.subscriptionError,
        {},
        error
      );

      trackEvent({
        ctaName: PRO_RATED_UPGRADE_MESSAGES.trackName,
        properties: {
          plan_selected: Constants.ANNUAL_PLAN_CODE,
          status: 'error',
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const { loggedIn } = userInfo;
  const userPlanCode = userInfo?.user.planCode;

  if (!loggedIn) {
    redirectToLogin();
    return null;
  }

  if (loggedIn && userPlanCode === Constants.ANNUAL_PLAN_CODE) {
    redirectToBaseSite();
    return null;
  }

  return (
    <Layout
      isLoading={loading || isLoading}
      showError={showRetry}
      redirectIfNoAuth
      ErrorComponent={() => (
        <AllErrorMessage subjectAction="upgrade" onButtonClick={onClose} />
      )}
    >
      <ProRatedUpgrade
        submitUpgrade={onSubmitSubscription}
        loading={loading}
        annualPrice={prices.annualPrice?.annualPrice}
        seasonalPrice={prices.seasonalPrice?.price}
      />
      <HCaptchaContainer ref={captchaRef} />
      <SubscriptionUpdatePaymentModal
        onClose={onClose}
        subtitle={errorPaymentMessage}
        isOpen={showPayment}
        onSuccess={onSubmitSubscription}
        hasError={!!errorPaymentMessage}
      />
    </Layout>
  );
}

export default ProRatedUpgradeContainer;
